import React, { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { getCameraSnapshotMonitoring, postSetCameraActivationZoneMonitoring } from '../AdminAlert/alertApi';
import { geojsonToWKT, wktToGeoJSON } from '@terraformer/wkt';
import type { Point, Polygon } from './gridTypes';
import { usePolygonDrag } from './usePolygonDrag';
import { MonitorAlertEventDto } from '@/app/Alert/types';

interface GridEditorProps {
  /** Full alert event or a subset containing activationZone + identifiers */
  event: MonitorAlertEventDto; // expects videoProcessorId & activationZone present
  onSave?: () => void;
  initialPoints?: Point[];
  width?: number;
  height?: number;
}

// We previously relied on AssetVideoConfigDto; now we derive camera info from the event
export function GridEditor({
  event,
  onSave,
  width = 780,
  height = 500,
}: GridEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [hoveredPoint, setHoveredPoint] = useState<Point | null>(null);
  const [hoveredPolygonIndex, setHoveredPolygonIndex] = useState<number>(-1);
  const [shadowLine, setShadowLine] = useState<{ start: Point; end: Point } | null>(null);
  const [cameraId, setCameraId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [initialGrid, setInitialGrid] = useState(false);
  const [scaledHeight, setScaledHeight] = useState(height);
  const [currentPolygonIndex, setCurrentPolygonIndex] = useState(-1);
  const [polygons, setPolygons] = useState<Polygon[]>([{ points: [], closed: false }]);
  const { dragging, startDrag, updateDrag, endDrag } = usePolygonDrag(setPolygons);

  const loadImageAndGrid = async () => {
    const videoProcessorConfigId = event.videoProcessorIdentifier || '';
    setCameraId(videoProcessorConfigId);

    const snapshotUrls: string[] = (event.values || [])
      .filter((v: any) => v && v.type === 'SnapshotImage' && v.sourceUrl)
      .map((v: any) => v.sourceUrl);

    if (snapshotUrls.length > 0) {
      const img = new Image();
      img.src = snapshotUrls[0];
      img.onload = () => {
        setImage(img);
        const scale = width / img.width;
        const newScaledHeight = img.height * scale;
        setScaledHeight(newScaledHeight);

        if (canvasRef.current) {
          canvasRef.current.width = width;
          canvasRef.current.height = newScaledHeight;
        }

        setIsLoading(false);

        if (event.activationZone) {
          let geojson = wktToGeoJSON(event.activationZone);

          if (geojson.type === 'MultiPolygon') {
            setInitialGrid(true);
            const loadedPolygons: Polygon[] = geojson.coordinates.map((c) => {
              const polygonPoints: Point[] = [];
              c[0].forEach((coord) => {
                polygonPoints.push({
                  x: coord[0] * width,
                  y: coord[1] * newScaledHeight,
                });
              });
              return { points: polygonPoints, closed: true };
            });
            setPolygons(loadedPolygons);
          }
        }
      };
      img.onerror = () => {
        setIsLoading(false);
      };
      return; // Done loading via snapshotUrls
    } else {
      // We could attempt to load a snapshot via API if no URL present but for now just log error
      console.error('No snapshot URLs found for event:', event.id);
    }
  };

  // Load and scale image
  useEffect(() => {
    setIsLoading(true);
    loadImageAndGrid();
  }, [event]);

  // Draw function
  const draw = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !image || !canvas) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw image
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    // Draw all polygons
    polygons.forEach((polygon, index) => {
      if (polygon.points.length > 0) {
        ctx.beginPath();
        ctx.moveTo(polygon.points[0].x, polygon.points[0].y);
        polygon.points.forEach((point) => {
          ctx.lineTo(point.x, point.y);
        });

        if (polygon.closed) {
          ctx.closePath();
        }

        // Use green only for actively drawing polygon
        const isDrawing = index === polygons.length - 1 && !polygon.closed;
        ctx.strokeStyle = isDrawing ? 'rgba(0,128,0,1)' : 'rgba(255,0,0,1)';
        // Increase line width for selected polygon and hovered polygon

        if (!isDrawing && polygon.closed && index === currentPolygonIndex) {
          ctx.strokeStyle = 'rgba(255,255,255,1)';
        }

        ctx.lineWidth = index === currentPolygonIndex ? 3 : index === hoveredPolygonIndex ? 2 : 1;
        ctx.stroke();

        if (polygon.closed) {
          ctx.fillStyle =
            index === currentPolygonIndex ? 'rgba(50, 168, 82, 0.3)' : 'rgba(200,0,0,0.3)';
          ctx.fill();
        }

        // Draw points for current polygon only
        if (index === currentPolygonIndex) {
          polygon.points.forEach((point, pointIndex) => {
            ctx.beginPath();
            const isFirstPoint = pointIndex === 0;
            const isHovered = isFirstPoint && hoveredPoint;
            const radius = isHovered ? 10 : 5;

            ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI);
            ctx.fillStyle = isHovered ? 'green' : 'white';
            ctx.strokeStyle = isHovered ? 'rgba(0,255,0,1)' : polygon.closed ? 'white' : 'black';
            ctx.lineWidth = 2;
            ctx.fill();
            ctx.stroke();
          });
        }
      }
    });

    if (currentPolygonIndex !== -1) {
      const currentPolygon = polygons[currentPolygonIndex];
      if (shadowLine && !currentPolygon.closed) {
        ctx.beginPath();
        ctx.moveTo(shadowLine.start.x, shadowLine.start.y);
        ctx.lineTo(shadowLine.end.x, shadowLine.end.y);
        ctx.strokeStyle = 'rgba(0,128,0,1)';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }
  };

  // Draw on every update
  useEffect(() => {
    draw();
  }, [polygons, hoveredPoint, shadowLine, image, currentPolygonIndex]);

  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const isNearPoint = (p1: Point, p2: Point, threshold = 20): boolean => {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;
    return Math.sqrt(dx * dx + dy * dy) < threshold;
  };

  // Check if a point is inside a polygon using ray casting algorithm
  const isPointInPolygon = (point: Point, polygon: Point[]): boolean => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x;
      const yi = polygon[i].y;
      const xj = polygon[j].x;
      const yj = polygon[j].y;

      const intersect =
        yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // Find which polygon was clicked
  const findClickedPolygon = (pos: Point): number => {
    // Check polygons in reverse order (top to bottom visually)
    for (let i = polygons.length - 1; i >= 0; i--) {
      const polygon = polygons[i];
      if (polygon.closed && isPointInPolygon(pos, polygon.points)) {
        return i;
      }
    }
    return -1;
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e);
    let currentPolygon = null;

    if (dragging) {
      updateDrag(pos);
      return;
    }

    if (currentPolygonIndex !== -1) {
      currentPolygon = polygons[currentPolygonIndex];
    }

    // Update cursor based on what's being hovered
    const canvas = canvasRef.current;
    if (canvas) {
      const hoveredIndex = findClickedPolygon(pos);
      setHoveredPolygonIndex(hoveredIndex);

      if (currentPolygon && !currentPolygon.closed && currentPolygon.points.length > 0) {
        // Only show crosshair when actively drawing and have at least one point
        canvas.style.cursor = 'crosshair';
      } else if (hoveredIndex !== -1) {
        // Hovering over a polygon
        canvas.style.cursor = 'pointer';
      } else {
        // Default cursor
        canvas.style.cursor = 'default';
      }
    }

    // Check if near starting point and we have enough points to close
    if (
      currentPolygon &&
      !currentPolygon.closed &&
      currentPolygon.points.length > 2 &&
      isNearPoint(pos, currentPolygon.points[0])
    ) {
      setHoveredPoint(currentPolygon.points[0]);
      setShadowLine({
        start: currentPolygon.points[currentPolygon.points.length - 1],
        end: currentPolygon.points[0],
      });
    } else {
      setHoveredPoint(null);
      if (currentPolygon && !currentPolygon.closed && currentPolygon.points.length > 0) {
        setShadowLine({
          start: currentPolygon.points[currentPolygon.points.length - 1],
          end: pos,
        });
      } else {
        setShadowLine(null);
      }
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e);
    let currentPolygon = null;

    // check for dragging existing point
    for (let i = 0; i < polygons.length; i++) {
      const poly = polygons[i];
      if (poly.closed) {
        const idx = poly.points.findIndex((p) => isNearPoint(pos, p));
        if (idx !== -1) {
          setCurrentPolygonIndex(i);
          startDrag(i, idx);
          return;
        }
      }
    }

    if (currentPolygonIndex !== -1) {
      currentPolygon = polygons[currentPolygonIndex];
    }

    // If we have a closed polygon or haven't started drawing yet
    if (
      currentPolygonIndex === -1 ||
      currentPolygon!.closed ||
      currentPolygon!.points.length === 0
    ) {
      const clickedPolygonIndex = findClickedPolygon(pos);
      if (clickedPolygonIndex !== -1) {
        // Select the clicked polygon
        setCurrentPolygonIndex(clickedPolygonIndex);
        return;
      }

      // Start a new polygon if we clicked in empty space
      if (currentPolygonIndex === -1 || currentPolygon!.closed) {
        setPolygons([...polygons, { points: [pos], closed: false }]);
        setCurrentPolygonIndex(polygons.length);
        setInitialGrid(false);
        return;
      } else {
        // Start the first point of the current empty polygon
        const updatedPolygons = [...polygons];
        updatedPolygons[currentPolygonIndex] = {
          ...currentPolygon,
          points: [pos],
          closed: false,
        };
        setPolygons(updatedPolygons);
        return;
      }
    }

    // Handle closing the polygon
    if (
      currentPolygon &&
      currentPolygon.points.length > 2 &&
      hoveredPoint &&
      isNearPoint(pos, currentPolygon.points[0])
    ) {
      // Complete the current polygon
      const updatedPolygons = [...polygons];
      updatedPolygons[currentPolygonIndex] = {
        points: [...currentPolygon.points, currentPolygon.points[0]],
        closed: true,
      };
      setPolygons(updatedPolygons);
      setShadowLine(null);
      setHoveredPoint(null);
      setCurrentPolygonIndex(-1);

      // Reset cursor to default immediately
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.style.cursor = 'default';
      }
    } else if (currentPolygon) {
      // Add a new point to the current polygon
      const updatedPolygons = [...polygons];
      updatedPolygons[currentPolygonIndex] = {
        ...currentPolygon,
        points: [...currentPolygon.points, pos],
      };
      setPolygons(updatedPolygons);
    }
  };

  const handleMouseUp = () => {
    endDrag();
  };

  const handleReset = () => {
    setPolygons([]);
    setCurrentPolygonIndex(-1);
    setShadowLine(null);
    setHoveredPoint(null);
    setInitialGrid(false);
  };

  const handleDeletePolygon = (index: number) => {
    const updatedPolygons = polygons.filter((_, i) => i !== index);
    setPolygons(updatedPolygons.length > 0 ? updatedPolygons : [{ points: [], closed: false }]);
    setCurrentPolygonIndex(-1);
    setInitialGrid(false);
  };

  const pointsToMultiPolygon = (polygons: Polygon[]): string => {
    if (!canvasRef.current) return '';

    const canvas = canvasRef.current;
    const closedPolygons = polygons.filter((p) => p.closed && p.points.length >= 3);

    if (closedPolygons.length === 0) return '';

    const normalizedPolygons = closedPolygons.map((polygon) => [
      polygon.points.map((point) => [point.x / canvas.width, point.y / canvas.height]),
    ]);

    // Create GeoJSON polygon format
    const geojson: GeoJSON.MultiPolygon = {
      type: 'MultiPolygon',
      coordinates: normalizedPolygons,
    };

    return geojsonToWKT(geojson);
  };

  const saveGrid = async () => {
    let body = { wkt: '' };

    if (polygons.length !== 0) {
      const multiPolygonString = pointsToMultiPolygon(polygons);
      body.wkt = multiPolygonString;
    }

    await postSetCameraActivationZoneMonitoring(cameraId, body);
    setIsSaving(false);

    if (onSave) {
      onSave();
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveGrid();
    } catch (error) {
      console.error('Error saving grid:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const disableSave = () => {
    if (initialGrid || isSaving) return true;

    // Resetting is fine
    if (polygons.length === 0) return false;

    return !polygons.some((p) => p.closed && p.points.length >= 3);
  };

  return (
    <Card className="p-4">
      <div className="flex flex-col gap-4">
        <div>
          <div className="mb-2 grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Camera Name</label>
              <p className="text-sm">{event.nvrDeviceName || 'Unknown Device'}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Camera ID</label>
              <p className="text-sm">{event.videoProcessorIdentifier || '—'}</p>
            </div>
          </div>
          <div className="flex flex-col gap-1 text-sm">
            <div className="flex items-center gap-1">
              <div
                className="h-4 w-4"
                style={{
                  backgroundColor: 'rgba(200,0,0,0.3)',
                  border: '1px solid rgba(255,0,0,1)',
                }}
              />
              <span>Monitored Area</span>
            </div>
            <p className="text-xs text-muted-foreground">
              The shaded region is monitored
            </p>
          </div>
        </div>
        <div className="relative">
          <canvas
            ref={canvasRef}
            width={width}
            height={scaledHeight}
            className="block max-h-full max-w-full"
            onMouseMove={handleMouseMove}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
          />
        </div>
      </div>
      <div className="mt-4 flex justify-between">
        <div>
          <Button
            disabled={currentPolygonIndex === -1}
            onClick={() => handleDeletePolygon(currentPolygonIndex)}
          >
            Delete Selected Zone
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="destructive" onClick={handleReset}>
            Reset
          </Button>
          <Button onClick={handleSave} disabled={disableSave()}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
}
